import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtAUD, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Pill, STAGE_STYLES } from "@/components/badges";
import DateRangePicker from "@/components/DateRangePicker";
import { Download, Search, Trash2, AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const STAGES = ["Need Assessment", "Formal Presentation", "Decision Pending", "Closed Won", "Closed Lost"];

export default function Opportunities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [opps, setOpps] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [stage, setStage] = useState(searchParams.get("stage") || "");
  const [activeOnly, setActiveOnly] = useState(searchParams.get("active") === "1");
  const [assignedTo, setAssignedTo] = useState(searchParams.get("assigned_to") || "");
  const [dateRange, setDateRange] = useState({
    from: searchParams.get("date_from") || "",
    to: searchParams.get("date_to") || "",
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("eyb_opps_view") || "list"; } catch { return "list"; }
  });
  useEffect(() => { try { localStorage.setItem("eyb_opps_view", view); } catch { /* ignore */ } }, [view]);

  // sync URL
  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (stage) p.set("stage", stage);
    if (activeOnly) p.set("active", "1");
    if (assignedTo) p.set("assigned_to", assignedTo);
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line
  }, [q, stage, activeOnly, assignedTo, dateRange.from, dateRange.to]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (stage) params.stage = stage;
    if (assignedTo) params.assigned_to = assignedTo;
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    const [o, u] = await Promise.all([api.get("/opportunities", { params }), api.get("/users")]);
    let rows = o.data;
    if (activeOnly && !stage) {
      rows = rows.filter((r) => r.stage !== "Closed Won" && r.stage !== "Closed Lost");
    }
    setOpps(rows); setUsers(u.data); setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, stage, activeOnly, assignedTo, dateRange.from, dateRange.to]);

  const updateStage = async (id, newStage) => {
    try {
      await api.patch(`/opportunities/${id}`, { stage: newStage });
      load();
      toast.success(`Moved to ${newStage}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("eyb_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/opportunities.csv`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "opportunities.csv"; a.click();
  };

  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const selectedOpps = useMemo(() => opps.filter((o) => selected.has(o.id)), [opps, selected]);
  const selectedTotal = selectedOpps.reduce((s, o) => s + (Number(o.estimated_value) || 0), 0);

  const toggleOne = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((cur) => (cur.size === opps.length ? new Set() : new Set(opps.map((o) => o.id))));
  };
  const clearSelection = () => setSelected(new Set());

  return (
    <div className="p-8" data-testid="opportunities-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Opportunities</h1>
          <p className="text-sm text-slate-500 mt-1">{opps.length} {opps.length === 1 ? "opportunity" : "opportunities"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="all" />
          <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="opps-view-toggle">
            <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-1.5 text-xs font-semibold ${view === "list" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}>List</button>
            <button data-testid="view-board" onClick={() => setView("board")} className={`px-3 py-1.5 text-xs font-semibold ${view === "board" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}>Board</button>
          </div>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {isAdmin && selected.size > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="opps-bulk-bar">
          <div className="text-sm text-slate-800">
            <span className="font-semibold">{selected.size}</span> selected · combined value <span className="font-semibold">{fmtAUD(selectedTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="opps-bulk-clear"
              onClick={clearSelection}
              className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 rounded-md"
            >
              Clear
            </button>
            <button
              data-testid="opps-bulk-delete-btn"
              onClick={() => setShowBulkDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="eyb-card p-4 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
        </div>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            data-testid="filter-active-only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            disabled={!!stage}
          />
          <span className={stage ? "text-slate-400" : "text-slate-700"}>Active only</span>
        </label>
        <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">All owners</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-x-auto">
          {STAGES.map((s) => {
            const items = opps.filter((o) => o.stage === s);
            const total = items.reduce((a, b) => a + (Number(b.estimated_value) || 0), 0);
            return (
              <div key={s} className="bg-slate-50 rounded-xl p-3 min-h-[200px]" data-testid={`stage-col-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div>
                    <div className="text-xs font-bold text-slate-800">{s}</div>
                    <div className="text-[10px] text-slate-500">{items.length} · {fmtAUD(total)}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((o) => (
                    <div key={o.id} className="bg-white rounded-lg p-3 border border-slate-200 hover:border-[#29ABE2]/50 transition-colors">
                      <Link to={`/opportunities/${o.id}`} className="font-semibold text-sm text-slate-900 hover:text-[#1f8ebf]">{o.name}</Link>
                      <div className="text-[11px] text-slate-500 mt-1">{o.business_name}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-bold text-slate-900">{fmtAUD(o.estimated_value)}</span>
                        {o.stage === "Closed Won" && <span className="text-[10px] font-semibold text-emerald-700">Won</span>}
                      </div>
                      <select value={o.stage} onChange={(e) => updateStage(o.id, e.target.value)} className="mt-2 w-full text-[11px] border border-slate-200 rounded px-1.5 py-1 bg-white">
                        {STAGES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-[11px] text-slate-400 text-center py-4">No opportunities</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="eyb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
                {isAdmin && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      data-testid="opps-select-all"
                      checked={opps.length > 0 && selected.size === opps.length}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-semibold">Opportunity</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Value</th>
                <th className="px-4 py-3 font-semibold">Close Date</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={isAdmin ? 6 : 5} className="py-6 text-center text-slate-400 text-xs">Loading…</td></tr> :
                opps.length === 0 ? <tr><td colSpan={isAdmin ? 6 : 5} className="py-10 text-center text-slate-400 text-sm">No opportunities yet. Convert a lead to start.</td></tr> :
                opps.map((o) => (
                  <tr key={o.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${selected.has(o.id) ? "bg-rose-50/40" : ""}`}>
                    {isAdmin && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          data-testid={`opps-select-${o.id}`}
                          checked={selected.has(o.id)}
                          onChange={() => toggleOne(o.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3"><Link to={`/opportunities/${o.id}`} className="font-semibold text-slate-900 hover:text-[#1f8ebf]">{o.name}</Link><div className="text-xs text-slate-500">{o.business_name}</div></td>
                    <td className="px-4 py-3"><Pill className={STAGE_STYLES[o.stage]}>{o.stage}</Pill></td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{fmtAUD(o.estimated_value)}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{fmtDate(o.expected_close_date)}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{usersById[o.assigned_to]?.name || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showBulkDelete && (
        <BulkDeleteModal
          opps={selectedOpps}
          totalValue={selectedTotal}
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); clearSelection(); load(); }}
        />
      )}
    </div>
  );
}

function BulkDeleteModal({ opps, totalValue, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!confirmed) { toast.error("Please confirm you understand this is permanent"); return; }
    if (!password) { toast.error("Password is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/opportunities/bulk-delete", { ids: opps.map((o) => o.id), password });
      toast.success(`Deleted ${data.deleted} opportunit${data.deleted === 1 ? "y" : "ies"}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="opps-bulk-delete-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-rose-700">Bulk delete {opps.length} opportunit{opps.length === 1 ? "y" : "ies"}</DialogTitle>
              <DialogDescription>
                Combined value: <span className="font-semibold text-slate-900">{fmtAUD(totalValue)}</span>. This cannot be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="max-h-40 overflow-y-auto bg-slate-50 rounded-md border border-slate-200 px-3 py-2 text-xs space-y-1" data-testid="opps-bulk-delete-list">
            {opps.slice(0, 30).map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-2">
                <span className="truncate text-slate-800">{o.name}</span>
                <span className="text-slate-500 shrink-0">{fmtAUD(o.estimated_value)}</span>
              </div>
            ))}
            {opps.length > 30 && <div className="text-slate-500 pt-1 border-t border-slate-200">+ {opps.length - 30} more…</div>}
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="opps-bulk-confirm-checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span>I understand that this will permanently delete the selected opportunities along with their stage history and they cannot be recovered.</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-pwd" className="flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5 text-slate-500" />
              Confirm your admin password
            </Label>
            <Input
              id="bulk-pwd"
              data-testid="opps-bulk-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Your sign-in password"
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="opps-bulk-cancel">Cancel</Button>
            <Button
              type="submit"
              disabled={submitting || !confirmed || !password}
              data-testid="opps-bulk-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {submitting ? "Deleting…" : `Delete ${opps.length}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
